import React from "react";
import styled from "styled-components";
import { Icon, Button, Colors, Divider } from "@blueprintjs/core";
import { DropzoneRootProps, useDropzone } from "react-dropzone";
import { Atom, useAtom } from "klyva";
import { Box, Stack, Text } from "ui-components";
import produce from "immer";
import { useMutation } from "react-query";
import { getRgba } from "ui-lib";
import { postAssetMutation } from "ui-io/queries/assets/asset-mutation";
import { ReferenceT } from "ui-io/task-io";
import { TaskT } from "ui-io/task-io/task-t";

import toasts from "../../../utils/toasts";
import {
  formatBytes,
  getIconForContentType,
  getValidContentType,
} from "../../../utils/attachment-utilities";
import { validFileTypes } from "ui-io/assets-io/valid-file-type-io";
import { AcceptedFile } from ".";
import useSession from "ui-io/use-session";

type Props = {
  handleToggleUpload: () => void;
  acceptedFilesAtom: Atom<AcceptedFile[]>;
  task: TaskT;
};

const SECONDS_IN_A_YEAR = 365 * 24 * 60 * 60;
const MAX_FILE_SIZE_BYTES = 1e8; // ~100 MB

const UploadAttachmentOverlay = (props: Props) => {
  const { handleToggleUpload, acceptedFilesAtom, task } = props;
  const { session } = useSession();
  const environment = session?.environment ?? "prod";
  const [acceptedFiles, setAcceptedFiles] = useAtom(acceptedFilesAtom);
  const mutation = useMutation(postAssetMutation);

	const handleOnDropAccepted = React.useCallback(
		async <T extends File>(acceptedFiles: T[]) => {
			setAcceptedFiles((prev) =>
				prev.concat(acceptedFiles.map((file) => ({ file })))
			);

			try {
				const results = await mutation.mutateAsync({
					files: acceptedFiles,
					asset_type: "Resource/Attachments",
					resource_type: "AdminTicket",
					resource_id: task.id,
					expiry: 3 * SECONDS_IN_A_YEAR + Math.ceil(Date.now() / 1e3),
					creation_user: session?.username,
				});

				setAcceptedFiles((prev) =>
					produce(prev, (draft) => {
						acceptedFiles.forEach((file, index) => {
							const result = results[index];
							if (!result) return;

							const reference: ReferenceT = {
								reference_type: "URL" as const,
								url_reference: {
									url: result.fileUrl,
									assets_id: result.asset_id,
									name: result.name,
									file_name: file.name,
									description: file.name,
									type: result.fileUrl.split(".").pop(),
									fileUrl: result.fileUrl,
								},
								asset_properties: {
									asset_id: result.asset_id,
									resource_type: result.resource_type,
									resource_id: result.resource_id,
									creation_user: result.creation_user,
									creation_time: result.creation_time,
									content_type: result.content_type,
									filename: result.filename,
									filesize: result.filesize,
									app_id: result.app_id,
									asset_type: result.resource_type,
									name: result.name,
								},
							};
							const draftFile = draft.find((d) => d.file === file);
							if (draftFile) draftFile.reference = reference;
						});
					})
				);
			} catch (error) {
				acceptedFiles.forEach((file) =>
					toasts.error(`Error when uploading ${file.name}`)
				);
			}
		},
		[mutation, setAcceptedFiles, task, session]
	);


  const handleOnRejected = React.useCallback(
    (rejectedFiles: { file: File }[]) => {
      for (const rejectedFile of rejectedFiles) {
        const { file } = rejectedFile;
        toasts.error(
          `Unfortunately we do not accept the format of the file: ${file.name}`
        );
      }
    },
    []
  );

  const handleOnFileRemove = (index: number) => {
    setAcceptedFiles((prev) => {
      return produce(prev, (draft) => {
        draft.splice(index, 1);
      });
    });
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: validFileTypes,
    onDropAccepted: handleOnDropAccepted,
    onDropRejected: handleOnRejected,
    maxSize: MAX_FILE_SIZE_BYTES,
  });

  return (
    <Wrapper>
      <Button
        icon="cross"
        onClick={handleToggleUpload}
        outlined
        style={{
          position: "absolute",
          top: 20,
          right: 20,
        }}
      />
      <Icon icon="cloud-upload" iconSize={100} />
      <div className="container">
        <Container
          {...getRootProps({ isDragActive, isDragAccept, isDragReject })}
        >
          <input {...getInputProps()} />
          <p>Drag 'n' drop some files here, or click to select files</p>
        </Container>
        <Stack>
          {acceptedFiles.map(({ file, reference }, index) => {
            return (
              <AttachmentWrapper key={file.name}>
                <IconWrapper>
                  <Icon
                    icon={getIconForContentType(getValidContentType(file.type))}
                  />
                </IconWrapper>
                <TextContainer>
                  <DescriptionText>{file.name}</DescriptionText>
                  <TinyText>{formatBytes(file.size)}</TinyText>
                </TextContainer>
                <Divider style={{ borderColor: Colors.GRAY4 }} />
                <Box
                  style={{ alignSelf: "center" }}
                  onClick={() => handleOnFileRemove(index)}
                >
                  {reference ? (
                    <Button icon="trash" small minimal intent="danger" />
                  ) : (
                    <Button small minimal loading />
                  )}
                </Box>
              </AttachmentWrapper>
            );
          })}
        </Stack>
      </div>
    </Wrapper>
  );
};

const AttachmentWrapper = styled.div`
  padding: 14px;
  border-radius: 4px;
  width: 100%;
  display: flex;
  border-width: 1px;
  border-style: solid;
  border-color: ${(props) => {
    return props.theme.darkMode ? Colors.GRAY1 : Colors.GRAY5;
  }};
  background-color: ${(props) => {
    return props.theme.darkMode ? Colors.DARK_GRAY4 : Colors.LIGHT_GRAY4;
  }};
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 6px;
`;

const TinyText = styled.div`
  font-size: 10px;
`;

const DescriptionText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
`;

const IconWrapper = styled.div`
  padding: 6px;
  border-radius: 4px;
  align-self: flex-start;
  margin-right: 6px;
  background-color: ${(props) => {
    return props.theme.darkMode ? Colors.DARK_GRAY3 : Colors.LIGHT_GRAY5;
  }};
  color: ${(props) => {
    return props.theme.darkMode ? Colors.LIGHT_GRAY5 : Colors.DARK_GRAY3;
  }};
`;

const Container = styled.div<DropzoneRootProps>`
  flex: 1;
  background-color: ${(props) => {
    if (props.isDragAccept) {
      return getRgba(Colors.GREEN3, 0.25);
    }
    if (props.isDragReject) {
      return getRgba(Colors.RED3, 0.25);
    }
    if (props.isDragActive) {
      return getRgba(Colors.BLUE3, 0.25);
    }

    return props.theme.darkMode ? Colors.DARK_GRAY5 : Colors.LIGHT_GRAY5;
  }};
  color: ${(props) => {
    if (props.isDragAccept) {
      return Colors.GREEN3;
    }
    if (props.isDragReject) {
      return Colors.RED3;
    }
    if (props.isDragActive) {
      return Colors.BLUE3;
    }

    return props.theme.darkMode ? Colors.LIGHT_GRAY5 : Colors.DARK_GRAY5;
  }};
  border: 1px dashed #f5f8fa;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  border-color: currentColor;
  margin: 20px 0;
  border-radius: 3px;
  outline: none;
  transition: border 0.24s ease-in-out;

  & > p {
    margin: 0;
  }

  &:hover {
    cursor: pointer;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${(props) => {
    return props.theme.darkMode
      ? "rgba(57, 75, 89, 0.7)"
      : "rgba(245, 248, 250, 0.4)";
  }};
  backdrop-filter: blur(5px);
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 4;
`;

export default UploadAttachmentOverlay;
